// Neuigkeiten — das schwarze Brett des Hofs.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface NewsPost {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  published_at: string;
  author_id: string | null;
  author?: { full_name: string | null } | null;
  reads?: { profile_id: string }[];
}

// news_posts hängt über ZWEI Wege an profiles: einmal als Autor, einmal über
// die Lesemarken (news_reads). PostgREST kann das nicht auflösen und antwortet
// mit PGRST201 "ambiguous relationship" — die Beziehung MUSS benannt werden.
const AUTOR = "author:profiles!news_posts_author_id_fkey(full_name)";

export function useNews() {
  return useQuery({
    queryKey: ["news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_posts")
        .select(`*, ${AUTOR}, reads:news_reads(profile_id)`)
        .order("pinned", { ascending: false })
        .order("published_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as NewsPost[];
    },
  });
}

/** Zahl für den roten Punkt auf der Kachel. */
export function useUngeleseneNews() {
  return useQuery({
    queryKey: ["news-ungelesen"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const me = session.session?.user.id;
      if (!me) return 0;

      const { data, error } = await supabase
        .from("news_posts")
        .select("id, reads:news_reads(profile_id)")
        .limit(100);
      if (error) throw error;

      return (data ?? []).filter(
        (p: { reads?: { profile_id: string }[] }) =>
          !(p.reads ?? []).some((r) => r.profile_id === me)
      ).length;
    },
    staleTime: 30_000,
  });
}

/** Beitrag als gelesen markieren — verschwindet damit vom roten Punkt. */
export function useMarkiereGelesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postIds: string[]) => {
      if (!postIds.length) return;
      const { data: session } = await supabase.auth.getSession();
      const me = session.session?.user.id;
      if (!me) return;
      await supabase
        .from("news_reads")
        .upsert(
          postIds.map((post_id) => ({ post_id, profile_id: me })),
          { onConflict: "post_id,profile_id" }
        );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["news-ungelesen"] });
      qc.invalidateQueries({ queryKey: ["news"] });
    },
  });
}

export function useSpeichereNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id?: string; title: string; body: string; pinned: boolean }) => {
      const { data: session } = await supabase.auth.getSession();
      const { error } = await supabase.from("news_posts").upsert(
        {
          ...(p.id ? { id: p.id } : {}),
          title: p.title.trim(),
          body: p.body.trim(),
          pinned: p.pinned,
          author_id: session.session?.user.id ?? null,
        },
        { onConflict: "id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["news"] });
      qc.invalidateQueries({ queryKey: ["news-ungelesen"] });
    },
  });
}

export function useLoescheNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("news_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["news"] });
      qc.invalidateQueries({ queryKey: ["news-ungelesen"] });
    },
  });
}
